import { useCallback, useEffect } from "react";
import { type To, useNavigate } from "react-router";
import { confirmDialog } from "@/components/confirm-dialog";

const UNSAVED_CHANGES_DIALOG = {
  title: "Modifications non enregistrées",
  message:
    "Des modifications n'ont pas été enregistrées. Si vous quittez cette page, elles seront perdues.",
  confirmLabel: "Quitter sans enregistrer",
};

const DISCARD_DRAFT_DIALOG = {
  title: "Annuler les modifications",
  message:
    "Toutes les modifications non enregistrées vont être perdues et les cases reprendront leur valeur initiale.",
  confirmLabel: "Annuler les modifications",
  cancelLabel: "Revenir au tableau",
};

// Number of mounted guards holding a draft. Module-level so the menus and
// headers that navigate away can ask without being handed the flag.
let dirtyGuards = 0;
// Set while replaying a link click the user confirmed.
let replayingClick = false;

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Chrome requires returnValue to be set for the native prompt to show.
  event.returnValue = "";
};

const handleClick = (event: MouseEvent) => {
  if (replayingClick) return;
  // Modified or middle clicks open another tab: this page and its draft stay.
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest("a");
  if (!link || (link.target && link.target !== "_self")) return;
  if (link.hasAttribute("download")) return;
  const href = link.getAttribute("href");
  if (!href || href.startsWith("#")) return;

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search
  ) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  void confirmDialog(UNSAVED_CHANGES_DIALOG).then((confirmed) => {
    if (!confirmed) return;
    // Replaying the click lets the router <Link> keep its own replace/state.
    replayingClick = true;
    try {
      link.click();
    } finally {
      replayingClick = false;
    }
  });
};

function registerDirtyGuard(): () => void {
  dirtyGuards += 1;
  if (dirtyGuards === 1) {
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);
  }
  return () => {
    dirtyGuards -= 1;
    if (dirtyGuards === 0) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
    }
  };
}

/**
 * Warns before unsaved draft edits are lost, for screens using the
 * "brouillon + Enregistrer/Annuler" pattern (see AGENTS.md), while `isDirty`
 * is true:
 * - in-app `<Link>`/`<a>` clicks open the application popup and follow the
 *   link once the user confirms;
 * - `beforeunload` (refresh, close tab, typed URL) keeps the browser's own
 *   prompt, the only one browsers allow there.
 *
 * The app uses a declarative `BrowserRouter` (no `useBlocker`), so imperative
 * `navigate()` calls go through `useGuardedNavigate` or
 * `confirmLeaveWithUnsavedChanges` instead.
 */
export function useUnsavedChangesGuard(isDirty: boolean): void {
  useEffect(() => (isDirty ? registerDirtyGuard() : undefined), [isDirty]);
}

/**
 * Resolves `true` at once when no mounted guard holds a draft, otherwise asks
 * in the application popup. Use it before leaving a draft without a router
 * navigation (closing a panel, switching account).
 */
export function confirmLeaveWithUnsavedChanges(): Promise<boolean> {
  if (dirtyGuards === 0) return Promise.resolve(true);
  return confirmDialog(UNSAVED_CHANGES_DIALOG);
}

/** « Annuler » of a draft table: `discard` runs once the user confirms. */
export async function confirmDiscardDraft(discard: () => void): Promise<void> {
  if (await confirmDialog(DISCARD_DRAFT_DIALOG)) discard();
}

/**
 * `navigate()` that asks first when a mounted guard holds a draft. For every
 * button or menu entry that leaves the page (back arrows, yard menus, header).
 */
export function useGuardedNavigate(): (to: To) => void {
  const navigate = useNavigate();
  return useCallback(
    (to: To) => {
      // Without a draft, navigate synchronously like a plain navigate().
      if (dirtyGuards === 0) {
        navigate(to);
        return;
      }
      void confirmDialog(UNSAVED_CHANGES_DIALOG).then((confirmed) => {
        if (confirmed) navigate(to);
      });
    },
    [navigate],
  );
}
